import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LeadWizardStepper, type WizardStep } from '@/components/sales/wizard/LeadWizardStepper';
import { StepBillingNotice } from '@/components/sales/wizard/StepBillingNotice';
import {
  StepLeadConfirmation,
  type StatusPatch,
} from '@/components/sales/wizard/StepLeadConfirmation';
import { StepLeadDetails } from '@/components/sales/wizard/StepLeadDetails';
import { StepPaymentPlan } from '@/components/sales/wizard/StepPaymentPlan';
import { StepProductDetails } from '@/components/sales/wizard/StepProductDetails';
import { InfoCell } from '@/components/sales/wizard/shared';
import { useToast } from '@/components/ui';
import { Button } from '@/components/ui/button';
import { buildInitialForm, type LeadDetailsForm } from '@/features/sales-rm/leadForm';
import {
  useLookupArn,
  useLookupGst,
  useOnboardLead,
  usePaymentInstallments,
  useProducts,
  useSaveLeadDetails,
  useSaveProductDetails,
  useUpdateLead,
} from '@/hooks';
import useAuth from '@/hooks/useAuth';
import { useBankAccounts } from '@/hooks/useCompanySettings';
import {
  getAumPlanTier,
  getCompleteAdditionalArns,
  useAumProductMap,
  useCalculateSetupQuote,
  usePricingConfig,
} from '@/hooks/usePricing';
import {
  leadFullName,
  type AdditionalArnInput,
  type ApiSubItem,
  type LeadRecord,
  type PaymentMode,
  type PayStatus,
  type ProductDiscount,
  type SetupQuoteResult,
} from '@/lib/sales-metrics';
import { type UpdateLeadPayload } from '@/services/leads.service';
import { errorMessage } from '@/utils/apiError';
import cn from '@/utils/cn';
import { ADD_ARN_NEEDS_AUM_MESSAGE, type RenewalFrequencyValue } from '@/utils/constants';
import { normalizeStateName } from '@/utils/indiaStates';
import { MULTI_YEAR_RENEWAL_DISCOUNT_PERCENT } from '@/utils/productTermPricing';

const STEPS: WizardStep[] = [
  { key: 0, title: 'Lead Details' },
  { key: 1, title: 'Product Details' },
  { key: 2, title: 'Lead Confirmation' },
  { key: 3, title: 'Payment Plan' },
  { key: 4, title: 'Billing Handover' },
];

const initialsOf = (name?: string): string =>
  (name ?? 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || 'U';

const VALID_EMAIL = /^\S+@\S+\.\S+$/;
const VALID_MOBILE = /^[6-9]\d{9}$/;

// Builds a patch to keep the lead's denormalized email/mobile (used by the
// wizard header, the leads table and the confirmation email's To) in sync with
// the Step 1 primary contact fields - only for valid values that actually
// differ from what the lead already stores.
const buildLeadContactPatch = (
  primaryEmail: string,
  primaryNumber: string,
  current: { email: string; mobile: string },
): { email?: string; mobile?: string } => {
  const patch: { email?: string; mobile?: string } = {};
  // Case-insensitive: the server lowercases email on save, so a mixed-case
  // entry must not look "different" every time and re-trigger the patch.
  if (
    primaryEmail &&
    primaryEmail.toLowerCase() !== current.email.toLowerCase() &&
    VALID_EMAIL.test(primaryEmail)
  ) {
    patch.email = primaryEmail;
  }
  if (primaryNumber && primaryNumber !== current.mobile && VALID_MOBILE.test(primaryNumber)) {
    patch.mobile = primaryNumber;
  }
  return patch;
};

interface LeadWizardProps {
  lead: LeadRecord;
  onBack: () => void;
  onOnboarded: () => void;
  backLabel?: string;
}

const LeadWizard = ({
  lead: initialLead,
  onBack,
  onOnboarded,
  backLabel = 'Back to leads',
}: LeadWizardProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Re-render the step email composers from the latest saved data. The preview
  // query is keyed by template + lead + bank; invalidating the whole family
  // refetches whichever step's preview is mounted, and applyPreview keeps the
  // RM's own edited blocks/fields (Fix 3/5: refresh on product, payment mode,
  // transaction, and bank changes).
  const refreshEmailPreview = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['email-preview'] });
  }, [queryClient]);
  const { data: catalog = [] } = useProducts();
  const productNamesByCode = useMemo(
    () => Object.fromEntries(catalog.map((p) => [p.code, p.name])),
    [catalog],
  );
  const updateLead = useUpdateLead();
  const onboardLead = useOnboardLead();
  const lookupArn = useLookupArn();
  const lookupGst = useLookupGst();
  const calculateSetupQuote = useCalculateSetupQuote();
  const { data: pricingConfig } = usePricingConfig();
  const renewalDiscountPercent =
    pricingConfig?.multiYearRenewalDiscountPercent ?? MULTI_YEAR_RENEWAL_DISCOUNT_PERCENT;

  // Onboarding position is restored entirely from relational data (ADR-020):
  // the highest step reached comes from the leads.stepper column, and an
  // onboarded client reopens with every step complete (ADR-011). There is no
  // wizard JSON draft anymore. The migration defines stepper as
  // (highest completed step index + 1) - see addLeadDetailsTable#stepperFor and
  // markDone below - so stepper N (N>=2) means steps 0..N-1 are done and the
  // user had advanced to step N. stepper 1 is ambiguous (a brand-new lead and a
  // lead with only Step 1 done both store 1, since the ENUM has no 0), so it
  // safely reopens on Step 1 with nothing pre-marked.
  const stepperNum = Math.min(5, Math.max(1, initialLead.stepper ?? 1));
  const lastStepIndex = STEPS.length - 1;
  // Completion is a property of the ORDER, not the client (ADR-048). Keying this
  // off `leadStatus === 'Client'` marked every step of a brand-new add-on order
  // green and dropped the RM straight onto Billing Handover, before they had
  // chosen any products - so Step 2 was skipped and the later steps rejected the
  // empty cart. A returning client's NEW order starts at Step 1 like any other.
  const isOrderConfirmed = Boolean(initialLead.orderOnboardedAt);
  const initialDone = isOrderConfirmed
    ? [0, 1, 2, 3, 4]
    : Array.from({ length: stepperNum === 1 ? 0 : stepperNum }, (_, i) => i);
  const initialActiveStep = isOrderConfirmed
    ? lastStepIndex
    : Math.min(stepperNum === 1 ? 0 : stepperNum, lastStepIndex);

  const [lead, setLead] = useState<LeadRecord>(initialLead);
  const [activeStep, setActiveStep] = useState(initialActiveStep);
  const [doneSteps, setDoneSteps] = useState<Set<number>>(new Set(initialDone));
  // Step 1 details load from the lead_details record (initialLead.form) and save
  // live to their own table (ADR-017).
  const [form, setForm] = useState<LeadDetailsForm>(() => buildInitialForm(initialLead));
  // Step 2 product selection/config loads from the product_details record and
  // saves live to its own table (ADR-018).
  const [selectedProducts, setSelectedProducts] = useState<string[]>(initialLead.selectedProducts);
  // Per-product discounts (ADR-019) load from product_details and save through
  // its own endpoint, like custom prices / terms.
  const [productDiscounts, setProductDiscounts] = useState<Record<string, ProductDiscount>>(
    initialLead.productDiscounts ?? {},
  );
  // Per-line override of the multi-year discount on years 2+. Loads from and
  // saves to product_details alongside the per-product discount above; a code
  // absent from the map is priced with the company-wide setting.
  const [productMultiYearDiscounts, setProductMultiYearDiscounts] = useState<
    Record<string, number>
  >(initialLead.productMultiYearDiscounts ?? {});
  // Additional ARNs (ADD_ARN) load from the lead_additional_arns table and save
  // live through the product-details endpoint (ADR-020).
  const [additionalArns, setAdditionalArns] = useState<AdditionalArnInput[]>(
    initialLead.additionalArns ?? [],
  );
  // Sub-item selections for API bundle products (API_MF_RESEARCH, API_CALCULATOR).
  // Keyed by product code; stored in product_details.metadata server-side.
  const [apiItemSelections, setApiItemSelections] = useState<Record<string, ApiSubItem[]>>(
    initialLead.apiItems ?? {},
  );
  // Chosen company bank account, shared by Step 3 (quotation) and Step 4
  // (billing) so the same bank's details appear in both emails (ADR-020).
  const { data: bankOptions = [] } = useBankAccounts();
  const [bankLabel, setBankLabel] = useState<string>(initialLead.selectedBankLabel ?? '');
  useEffect(() => {
    if (!bankLabel && bankOptions.length > 0) setBankLabel(bankOptions[0].label);
  }, [bankOptions, bankLabel]);
  // AUM is sourced from Step 1 (lead_details.arnAum, in crores) and shown
  // read-only in Step 2 (ADR-017). Derived, never separately editable.
  //
  // NULL means "not entered yet"; 0 means the RM typed a zero, which is a real
  // AUM that prices at the lowest slab. `Number(x) || 0` collapsed both to 0,
  // so a client with genuinely no AUM could never get past Step 2 - the save
  // guard read the 0 as an empty field and refused.
  const primaryArnAum = useMemo(() => {
    const raw = String(form.arnAum ?? '').trim();
    if (raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }, [form.arnAum]);
  const [customPrices, setCustomPrices] = useState<Record<string, number>>(
    initialLead.customPrices ?? {},
  );
  const [productTerms, setProductTerms] = useState<Record<string, number>>(
    initialLead.productTerms ?? {},
  );
  // Per-product renewal cadence (ADR-048), chosen in Step 4 and saved through
  // the same product-details endpoint as the rest of Step 2.
  const [productRenewalFrequencies, setProductRenewalFrequencies] = useState<
    Record<string, RenewalFrequencyValue>
  >(initialLead.productRenewalFrequencies ?? {});
  const [setupQuote, setSetupQuote] = useState<SetupQuoteResult | null>(null);
  const saveDetails = useSaveLeadDetails();
  const saveProductDetails = useSaveProductDetails();
  const skipNextDetailsSave = useRef(true);
  const skipNextProductSave = useRef(true);

  // Once any installment has a recorded transaction (paid) or is handed over to
  // billing, the payable total is frozen, so the product selection/pricing can no
  // longer change (the server rejects it, ADR-035). Mirror that in the UI: Step 2
  // shows a read-only banner and the autosave surfaces the server's message
  // instead of failing silently.
  const { data: installmentsForLock } = usePaymentInstallments(lead.id, lead.orderId);
  const productsLocked = (installmentsForLock ?? []).some(
    (r) => Boolean(r.handedOverAt) || (Boolean(r.transactionNo?.trim()) && Boolean(r.mode)),
  );

  const rm = useMemo(
    () => ({
      id: user?.id ?? 0,
      name: user?.name ?? 'Sales RM',
      email: user?.email ?? '',
      employeeCode: user?.employeeCode ?? null,
      rmId: `U-RM-${initialsOf(user?.name)}`,
    }),
    [user],
  );

  // When MFBOX products or AUM changes, fetch the slab-based setup quote.
  const aumMap = useAumProductMap();
  const planTier = getAumPlanTier(selectedProducts, aumMap);
  const hasAumProduct = planTier !== null;

  useEffect(() => {
    if (selectedProducts.length === 0) {
      setSetupQuote(null);
      return undefined;
    }

    const hasFlatProduct = catalog.some(
      (p) => selectedProducts.includes(p.code) && !aumMap[p.code] && p.code !== 'ADD_ARN',
    );
    // AUM = 0 is valid: an AUM-based product is still charged at the lowest slab,
    // so quote it whenever a figure has actually been entered.
    const canQuoteAum = hasAumProduct && primaryArnAum != null;
    // ADD_ARN alone is priceable in an add-on order (ADR-048): the AUM plan the
    // ARNs attach to normally lives on an earlier order, so requiring an AUM or
    // flat product here left an ARN-only cart with no quote at all.
    const hasAddArn = selectedProducts.includes('ADD_ARN');

    // Nothing priceable: no AUM product, no flat products and no ARNs.
    //
    // ADD_ARN alone is deliberately NOT short-circuited here (the 111 branch did
    // short-circuit it). The server can price a standalone ADD_ARN on an add-on
    // order - pricing.service#calculateSetupQuote charges the fee off
    // `hasAddArnProduct`, because an add-on order never carries the AUM line the
    // client bought in an earlier order, and keying the fee off the AUM line would
    // make every add-on ARN free. When the combination genuinely is unpriceable
    // (ADD_ARN with no AUM product anywhere), the server answers with an explicit
    // 422 naming the rule rather than a silent null, and Step 2 warns at the point
    // of entry before it ever gets that far (see StepProductDetails
    // #ADD_ARN_NEEDS_AUM_MESSAGE, whose wording mirrors that 422).
    if (!canQuoteAum && !hasFlatProduct && !hasAddArn) {
      setSetupQuote(null);
      return undefined;
    }

    // Send the raw selection; the server prices every line from the catalogue and
    // derives planTier / term / discounts itself (bug 2). primaryArnAum is sent
    // only when an AUM product is selected (the server ignores it otherwise).
    const handle = setTimeout(() => {
      calculateSetupQuote.mutate(
        {
          selectedProducts,
          customPrices,
          productTerms,
          productDiscounts,
          productMultiYearDiscounts,
          ...(canQuoteAum && primaryArnAum != null ? { primaryArnAum } : {}),
          additionalArns: getCompleteAdditionalArns(additionalArns),
          primaryArn: lead.arn,
          // Lets the server prorate an UPGRADE order instead of quoting the
          // higher tier at its full setup price.
          ...(lead.orderId != null ? { orderId: lead.orderId } : {}),
        },
        {
          onSuccess: (result) => setSetupQuote(result),
          onError: (err) => {
            setSetupQuote(null);
            const message = err.message || 'Could not calculate pricing';
            // SILENT for the ADD_ARN rule. This request and the product autosave
            // below both fire off the same keystroke and both come back with the
            // same 422, so toasting here stacked the identical sentence twice on
            // top of the yellow box Step 2 already shows at the point of entry.
            // The rule is stated there, and again when the RM actually tries to
            // quote or proceed (StepProductDetails). It is not a failure of this
            // request that needs its own announcement.
            if (message === ADD_ARN_NEEDS_AUM_MESSAGE) return;
            toast.error(message);
          },
        },
      );
    }, 400);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProducts,
    catalog,
    aumMap,
    hasAumProduct,
    primaryArnAum,
    productDiscounts,
    productMultiYearDiscounts,
    additionalArns,
    lead.arn,
    lead.orderId,
    customPrices,
    productTerms,
  ]);

  const setField = <K extends keyof LeadDetailsForm>(k: K, v: LeadDetailsForm[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  // Merges ONLY the fields a given patch changed (with the server's canonical
  // values) into prior lead state, instead of replacing the whole lead with a
  // full server snapshot. Several updateLead calls can be in flight at once -
  // e.g. {stepper} from Save & Proceed, {paymentMode} from Step 4, and
  // {email,mobile} from the Step 1 autosave. Replacing with a full snapshot let
  // whichever response landed last overwrite the others' fields with a snapshot
  // taken before they committed. Scoping each writer to its own keys via a
  // functional update is order-independent, so no writer can clobber another.
  const applyLeadPatch = (payload: UpdateLeadPayload, updated: LeadRecord): void => {
    const changed: Partial<LeadRecord> = {};
    (Object.keys(payload) as (keyof LeadRecord)[]).forEach((key) => {
      Object.assign(changed, { [key]: updated[key] });
    });
    setLead((prev) => ({ ...prev, ...changed }));
  };

  // Persist the Step 3 bank choice so Step 4 (Billing Handover) can show it
  // (Fix 5). Wraps setBankLabel: updates local state and the lead row.
  const handleSetBankLabel = (label: string) => {
    setBankLabel(label);
    if (label && label !== lead.selectedBankLabel) {
      const patch: UpdateLeadPayload = { selectedBankLabel: label };
      updateLead.mutate(
        { id: lead.id, payload: patch },
        { onSuccess: (updated) => applyLeadPatch(patch, updated) },
      );
      // No explicit email-preview invalidation needed: Step 3's composer preview
      // is keyed by bankLabel and refetches on this change, and Step 4 re-mounts
      // (staleTime 0) with the new lead.selectedBankLabel when the RM navigates to
      // it, so the billing handover email picks up the bank without a double fetch.
    }
  };

  // Client-type switch from Step 1 (ADKJ-117). -> Non-ARN lets the server mint
  // the 'NA…' code; -> ARN holder carries the typed ARN in the same request.
  // The server refuses the switch while AUM-based / ADD_ARN lines exist.
  const changeClientType = (nextIsNonArn: boolean, arn?: string) => {
    const patch: UpdateLeadPayload = nextIsNonArn ? { isNonArn: true } : { isNonArn: false, arn };
    updateLead.mutate(
      { id: lead.id, payload: patch },
      {
        onSuccess: (updated) => {
          // The arn changes server-side even when absent from the payload (the
          // minted code), so merge both keys explicitly rather than via the patch.
          setLead((prev) => ({ ...prev, isNonArn: updated.isNonArn, arn: updated.arn }));
          toast.success(
            nextIsNonArn
              ? `Marked as Non-ARN client (code ${updated.arn})`
              : 'Converted to ARN holder',
          );
        },
        onError: (err) => toast.error(err.message || 'Could not change the client type'),
      },
    );
  };

  const canGoTo = (i: number): boolean => {
    if (i <= activeStep || doneSteps.has(i)) return true;
    for (let k = 0; k < i; k += 1) {
      if (!doneSteps.has(k)) return false;
    }
    return true;
  };

  const goTo = (i: number) => {
    if (!canGoTo(i)) {
      toast.error('Complete the current step first');
      return;
    }
    setActiveStep(i);
  };

  const markDone = (i: number) => {
    setDoneSteps((prev) => new Set(prev).add(i));
    if (i === 0) {
      setForm((prev) => ({
        ...prev,
        createdDate: prev.createdDate || new Date().toISOString(),
        modifiedDate: new Date().toISOString(),
      }));
    }
    // Persist the highest step reached to the leads.stepper column (ADR-020).
    // The server never moves it backwards, so re-completing an earlier step is
    // harmless. stepper is 1-based; completing step index i means step i+1 done.
    const stepperPayload: UpdateLeadPayload = { stepper: i + 1 };
    updateLead.mutate(
      { id: lead.id, payload: stepperPayload },
      { onSuccess: (updated) => applyLeadPatch(stepperPayload, updated) },
    );
    if (i + 1 < STEPS.length) setActiveStep(i + 1);
  };

  // Debounced live save of Step 2 (Product Details) to the product_details
  // table on every selection/price/term/discount change, plus the additional
  // ARNs into lead_additional_arns (ADR-018/020). Skips the initial mount so
  // loading existing products doesn't immediately re-save them.
  useEffect(() => {
    if (skipNextProductSave.current) {
      skipNextProductSave.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      saveProductDetails.mutate(
        {
          id: lead.id,
          payload: {
            selectedProducts,
            customPrices,
            productTerms,
            productDiscounts,
            productMultiYearDiscounts,
            productRenewalFrequencies,
            additionalArns,
            apiItems: apiItemSelections,
          },
        },
        {
          // Products/discounts feed the quotation table in the Step 3/4 emails
          // (Fix 3: refresh the template after add/remove/edit).
          onSuccess: refreshEmailPreview,
          // Surface the server's rejection instead of failing silently - most
          // often the products-locked-after-handover guard (ADR-035).
          onError: (err) => {
            const message = errorMessage(err, 'Could not save product changes');
            // Silent for the ADD_ARN rule, like the quote above. This autosave is
            // debounced per keystroke and StrictMode runs the effect twice, so
            // toasting each refusal put the same sentence on screen two at a time
            // while the RM was still typing an ARN. The failed write is not
            // hidden: Step 2's note is on screen for exactly as long as the
            // server keeps refusing, and says the changes are not saved.
            if (message === ADD_ARN_NEEDS_AUM_MESSAGE) return;
            toast.error(message);
          },
        },
      );
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProducts,
    customPrices,
    productTerms,
    productDiscounts,
    productRenewalFrequencies,
    additionalArns,
    apiItemSelections,
  ]);

  // Debounced live save of Step 1 (Lead Details) to the lead_details table on
  // every edit (ADR-017). Skips the initial mount so loading existing details
  // doesn't immediately re-save them.
  useEffect(() => {
    if (skipNextDetailsSave.current) {
      skipNextDetailsSave.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      saveDetails.mutate({ id: lead.id, payload: form as unknown as Record<string, string> });
      // A manual Step 1 edit of the primary email/mobile must also update the
      // lead's denormalized copy (header, leads table, confirmation email To) -
      // the ARN auto-populate path already does this; a hand edit did not.
      const leadPatch = buildLeadContactPatch(form.primaryEmail, form.primaryNumber, lead);
      if (Object.keys(leadPatch).length > 0) {
        updateLead.mutate(
          { id: lead.id, payload: leadPatch },
          { onSuccess: (updated) => applyLeadPatch(leadPatch, updated) },
        );
      }
    }, 600);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // Persists a fetched Step 1 form snapshot to lead_details immediately,
  // bypassing the debounced autosave above. Without this, a fetch result
  // followed quickly by navigating away (e.g. back to the leads list) unmounts
  // the wizard before the 600ms debounce fires, and the pending save is dropped
  // by the effect's own cleanup (clearTimeout) - silently losing the fetch.
  const persistDetails = (nextForm: LeadDetailsForm) => {
    saveDetails.mutate({ id: lead.id, payload: nextForm as unknown as Record<string, string> });
  };

  // Fetches real ARN holder details (AdvisorKhoj MFAPI, falling back to AMFI
  // server-side - see arnLookup.service.ts). Only the fields the lookup
  // actually returns are filled in; everything else stays manual.
  const autoPopulate = () => {
    // A NON-ARN client's code is not an AMFI number - there is nothing to fetch.
    // The button is hidden for them; this guards direct calls.
    if (lead.isNonArn) {
      toast.error('Non-ARN clients have no AMFI record to fetch from');
      return;
    }
    lookupArn.mutate(
      { arn: lead.arn, excludeLeadId: lead.id },
      {
        onSuccess: (result) => {
          // The ARN API's contact details are treated as the source of truth:
          // if they differ from what the Sales RM entered when the lead was
          // created, the API value becomes primary and the RM's own entry is
          // kept as secondary instead of being discarded.
          let primaryEmail = result.primaryEmail || form.primaryEmail;
          let { secondaryEmail } = form;
          if (result.primaryEmail && result.primaryEmail !== lead.email) {
            primaryEmail = result.primaryEmail;
            secondaryEmail = lead.email || form.secondaryEmail;
          }

          let primaryNumber = result.primaryNumber || form.primaryNumber;
          let { secondaryNumber } = form;
          if (result.primaryNumber && result.primaryNumber !== lead.mobile) {
            primaryNumber = result.primaryNumber;
            secondaryNumber = lead.mobile || form.secondaryNumber;
          }

          const nextForm: LeadDetailsForm = {
            ...form,
            arnHolderName: result.arnHolderName || form.arnHolderName,
            // AUM in crores - 0 is a valid fetched value, so check for presence
            // rather than truthiness (a `||` check would treat 0 as missing).
            arnAum: result.arnAum !== undefined ? result.arnAum : form.arnAum,
            primaryNumber,
            secondaryNumber,
            primaryEmail,
            secondaryEmail,
            websiteUrl: result.websiteUrl || form.websiteUrl,
            // ARN holder's own address - distinct from the business's GST
            // address (`address`, filled in by the GST lookup below).
            arnAddress: result.address || form.arnAddress,
            remarks: result.remarks || form.remarks,
            createdDate: form.createdDate || new Date().toISOString(),
            modifiedDate: new Date().toISOString(),
          };
          setForm(nextForm);
          persistDetails(nextForm);

          // Fix 1: the server has just stamped these onto the lead row
          // (leads.service#recordArnCompliance, called by the lookup controller),
          // so mirror them onto the in-memory lead here. Without this the two
          // read-only fields in Step 1 would keep showing "Not fetched yet" until
          // the page was reloaded, which reads as a failed fetch.
          //
          // Each is applied only when the lookup actually carried it: `undefined`
          // means the source could not say - the public AMFI fallback returns
          // neither - and must not overwrite what a previous AdvisorKhoj fetch
          // established. This mirrors what the server does with the same values.
          if (result.kydCompliant !== undefined || result.arnValidTill !== undefined) {
            setLead((prev) => ({
              ...prev,
              ...(result.kydCompliant !== undefined ? { kydCompliant: result.kydCompliant } : {}),
              ...(result.arnValidTill !== undefined ? { arnValidTill: result.arnValidTill } : {}),
            }));
          }

          // Keep the lead's own email/mobile (header, leads table) in sync with
          // whichever value is now primary.
          const leadPatch = buildLeadContactPatch(primaryEmail, primaryNumber, lead);
          if (Object.keys(leadPatch).length > 0) {
            updateLead.mutate(
              { id: lead.id, payload: leadPatch },
              {
                onSuccess: (updated) => applyLeadPatch(leadPatch, updated),
              },
            );
          }

          let message = 'Details auto-populated from AMFI (fallback)';
          if (result.source === 'database') {
            message = 'Details matched from CRM database';
          } else if (result.source === 'advisorkhoj') {
            message = 'Details auto-populated from ARN';
          }
          toast.success(message);
        },
        onError: (err) => toast.error(err.message || 'Could not fetch details for this ARN'),
      },
    );
  };

  // Fetches Legal Name / Trade Name from the GSTIN, kept separate from the
  // ARN autofill above per the company's confirmed field availability.
  const autoPopulateGst = () => {
    if (!form.gstin) {
      toast.error('Enter a GSTIN first');
      return;
    }
    lookupGst.mutate(form.gstin, {
      onSuccess: (result) => {
        const nextForm: LeadDetailsForm = {
          ...form,
          legalName: result.legalName || form.legalName,
          displayName: result.displayName || form.displayName,
          pincode: result.pincode || form.pincode,
          city: result.city || form.city,
          state: normalizeStateName(result.state) || form.state,
          address: result.address || form.address,
          modifiedDate: new Date().toISOString(),
        };
        setForm(nextForm);
        persistDetails(nextForm);
        toast.success('Details auto-populated from GSTIN');
      },
      onError: (err) => toast.error(err.message || 'Could not fetch details for this GSTIN'),
    });
  };

  const handleUpdateLead = (patch: StatusPatch) => {
    updateLead.mutate(
      { id: lead.id, payload: patch },
      {
        onSuccess: (updated) => {
          applyLeadPatch(patch, updated);
          // Fix 3: a transaction reference / payment status change shows in the
          // billing handover email - refresh the template so it reflects it.
          refreshEmailPreview();
        },
        onError: () => toast.error('Could not update the lead'),
      },
    );
  };

  // Payment values are passed in from Step 4's own state (pay/amountPaid/mode)
  // rather than read from `lead`, which only reflects the last payment change
  // after its async PATCH resolves - a fast onboard click would otherwise
  // snapshot and re-persist stale payment status/amount. The server recomputes
  // the payable total, derives the price_at_sale snapshot and enforces the
  // payment policy, so only the selection + payment are sent (bug 1).
  const handleOnboard = (payment: {
    paymentStatus: PayStatus;
    paymentAmount: number;
    paymentMode: PaymentMode | null;
  }) => {
    if (selectedProducts.length === 0) {
      toast.error('Select at least one product in Step 2');
      return;
    }
    // Reopening an already-confirmed ORDER re-runs this same call to commit
    // edits rather than confirming a new sale (ADR-048) - a returning client is
    // already a Client, so the lead's status can't tell the two apart.
    const wasAlreadyConfirmed = Boolean(lead.orderOnboardedAt);
    onboardLead.mutate(
      {
        id: lead.id,
        payload: {
          selectedProducts,
          paymentStatus: payment.paymentStatus,
          paymentMode: payment.paymentMode,
          // Actual amount collected, taken from Step 4's live controls, not stale
          // state. The server bounds it to the payable total per the policy.
          paymentAmount: payment.paymentAmount,
        },
      },
      {
        onSuccess: () => {
          markDone(4);
          toast.success(
            wasAlreadyConfirmed ? 'Order updated' : 'Order confirmed - handed to Billing',
          );
          onOnboarded();
        },
        onError: () => toast.error('Could not save this client'),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 min-[1100px]:h-full min-[1100px]:min-h-0">
      <div className="shrink-0 rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-start gap-x-6 gap-y-2 text-sm">
            <InfoCell label="ID" value={String(lead.leadCode)} />
            <InfoCell label="Name" value={leadFullName(lead)} />
            <InfoCell label={lead.isNonArn ? 'Client Code' : 'ARN'} value={lead.arn} />
            <InfoCell label="Mobile" value={lead.mobile} />
            <InfoCell label="Email" value={lead.email} widthClass="max-w-[360px]" />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="shrink-0"
            aria-label={backLabel}
          >
            <ArrowLeft className="h-4 w-4 md:mr-1.5" />
            <span className="hidden md:inline">{backLabel}</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 min-[1100px]:min-h-0 min-[1100px]:flex-1 min-[1100px]:grid-cols-[220px_minmax(0,1fr)] min-[1100px]:grid-rows-[minmax(0,1fr)] min-[1100px]:items-stretch min-[1100px]:gap-6 min-[1100px]:overflow-hidden">
        <LeadWizardStepper
          steps={STEPS}
          activeStep={activeStep}
          doneSteps={doneSteps}
          onGoTo={goTo}
        />

        <div
          className={cn(
            'min-h-[300px] overflow-visible rounded-xl border border-border bg-card p-4 sm:p-6 min-[1100px]:h-full min-[1100px]:min-h-0',
            activeStep === 1 ? 'min-[1100px]:overflow-hidden' : 'min-[1100px]:overflow-y-auto',
          )}
        >
          {activeStep === 0 && (
            <StepLeadDetails
              form={form}
              setField={setField}
              onAutoPopulate={autoPopulate}
              isFetching={lookupArn.isPending}
              onAutoPopulateGst={autoPopulateGst}
              isFetchingGst={lookupGst.isPending}
              onSaveProceed={() => markDone(0)}
              isDone={doneSteps.has(0)}
              isNonArn={lead.isNonArn}
              clientCode={lead.arn}
              onChangeClientType={changeClientType}
              isChangingClientType={updateLead.isPending}
              kydCompliant={lead.kydCompliant ?? null}
              arnValidTill={lead.arnValidTill ?? null}
            />
          )}
          {activeStep === 1 && (
            <StepProductDetails
              leadId={lead.id}
              catalog={catalog}
              selected={selectedProducts}
              setSelected={setSelectedProducts}
              primaryArn={lead.arn}
              primaryArnAum={primaryArnAum}
              productDiscounts={productDiscounts}
              setProductDiscounts={setProductDiscounts}
              productMultiYearDiscounts={productMultiYearDiscounts}
              setProductMultiYearDiscounts={setProductMultiYearDiscounts}
              additionalArns={additionalArns}
              setAdditionalArns={setAdditionalArns}
              apiItemSelections={apiItemSelections}
              setApiItemSelections={setApiItemSelections}
              isNonArn={lead.isNonArn}
              setupQuote={setupQuote}
              isQuoteLoading={calculateSetupQuote.isPending}
              onSaveProceed={() => markDone(1)}
              isDone={doneSteps.has(1)}
              customPrices={customPrices}
              setCustomPrices={setCustomPrices}
              productTerms={productTerms}
              setProductTerms={setProductTerms}
              renewalDiscountPercent={renewalDiscountPercent}
              locked={productsLocked}
              orderId={lead.orderId ?? null}
            />
          )}
          {activeStep === 2 && (
            <StepLeadConfirmation
              lead={lead}
              form={form}
              selectedProducts={selectedProducts}
              catalog={catalog}
              rm={rm}
              isDone={doneSteps.has(2)}
              setupQuote={setupQuote}
              onUpdateLead={handleUpdateLead}
              onSaveProceed={() => markDone(2)}
              customPrices={customPrices}
              productTerms={productTerms}
              productDiscounts={productDiscounts}
              renewalDiscountPercent={renewalDiscountPercent}
              bankOptions={bankOptions}
              bankLabel={bankLabel}
              setBankLabel={handleSetBankLabel}
            />
          )}
          {activeStep === 3 && (
            <StepPaymentPlan
              leadId={lead.id}
              orderId={lead.orderId ?? null}
              setupQuote={setupQuote}
              isDone={doneSteps.has(3)}
              onSaveProceed={() => markDone(3)}
              selectedProducts={selectedProducts}
              productNames={productNamesByCode}
              productRenewalFrequencies={productRenewalFrequencies}
              onChangeProductFrequency={(code, value) =>
                setProductRenewalFrequencies((prev) => ({ ...prev, [code]: value }))
              }
            />
          )}
          {activeStep === 4 && (
            <StepBillingNotice
              lead={lead}
              selectedProducts={selectedProducts}
              catalog={catalog}
              setupQuote={setupQuote}
              rm={rm}
              isDone={doneSteps.has(4)}
              isOnboarding={onboardLead.isPending}
              onOnboard={handleOnboard}
              onUpdateLead={handleUpdateLead}
              customPrices={customPrices}
              productTerms={productTerms}
              renewalDiscountPercent={renewalDiscountPercent}
              bankLabel={bankLabel}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadWizard;
